/* eslint-disable max-len, complexity, max-lines, max-depth, max-statements */
import { requirejsOne } from '@ds/swymkit/tools'
import type { PlatformAPI_AMD } from '@ds/swymkit/typings'

import { router } from '~/router/router'

import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'
import { useMostRecentCollabActions } from '~/entities/container/conversation/stores/collab-events'
import { useConversationsStoreV1 } from '~/entities/container/conversation/stores/v1-conversations'
import { useConversationsStoreV2 } from '~/entities/container/conversation/stores/v2-conversations'
import type { IUser } from '~/entities/user/user/interfaces/user'
import { useUsersStore } from '~/entities/user/user/stores/users'
import { useUsersStoreV2 } from '~/entities/user/user/stores/v2-users'

import { AI_USER, AI_USER_FOR_IS_TYPING } from '~/features-swym/aura/config/ai'
import { useConversationsAURAStore } from '~/features-swym/aura/stores/conversations-aura'
import { listCommentsUris } from '~/features-swym/comment/api/comments'
import { isMultiWindowActive } from '~/features-swym/multi-window/utils/multi-window'

import { EContext } from '~/features-shared/config-manager/interfaces/contexts'
import { getPlatformId } from '~/features-shared/config-manager/stores/swym-config'
import { useContentsStore } from '~/features-shared/content/stores/contents'
import { useCommentsStoreV2 } from '~/features-shared/content/stores/v2-comments'
import { useContentsStoreV2 } from '~/features-shared/content/stores/v2-contents'
import { useLastVisitedConversation } from '~/features-shared/conversation/stores/conversation-states'
import { useFavoritesStore } from '~/features-shared/favorites/stores/favorites'
import { navigate } from '~/features-shared/left-panel/composables/left-panel-manager'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { logger } from '~/features-shared/libs/utils/logger'
import { getIdFromSubjectUri } from '~/features-shared/libs/utils/subject-uri-utils'
import { parseContent, toIConversation, toITimeline, toIUser, transformRTCFavoriteToConversation, transformRTCSectionToSection } from '~/features-shared/rtconv/api/formatters'
import { RTInterfaceAPI } from '~/features-shared/rtconv/api/rtinterface-api'
import {
  RTCMessengerAcceptLead,
  RTCMessengerCoReviewLeft,
  RTCMessengerCoReviewStop,
  RTCMessengerReplyToCoreview,
  startCatiaCollab,
} from '~/features-shared/rtconv/api/rtinterface-collab-response-handler'
import * as RTConv from '~/features-shared/rtconv/api/rtinterface-senders'
import type {
  RTCContentId,
  RTCConversation,
  RTCConversationId,
  RTCFavoriteConversation,
  RTCMessage,
  RTCMessageId,
  RTCSection,
  RTCSectionThread,
  RTCUser,
  RTCUserId,
} from '~/features-shared/rtconv/interfaces/rtconversation'
import type { IRTInterfaceResponseData, TxID } from '~/features-shared/rtconv/interfaces/rtinterface'
import { useSectionsStore } from '~/features-shared/sections/store/sections'
import {
  type IMessageDetailsInternalEvent,
  type ITimeline,
  TimelineStatus,
} from '~/features-shared/timeline/interfaces/timeline'

import type { Section } from '~/features-generic/left-panel/interfaces/left-panel'
import type { ModalConfirmProps } from '~/features-generic/modals/interfaces/modals'

/**
 * Handles 'newConversation' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.conversation - A newly created conversation.
 */
function newConversation(TxID: TxID, data?: { conversation: RTCConversation }) {
  const storeConversations = useConversationsStoreV2()
  const storeConversationsAURA = useConversationsAURAStore()
  const storeFavorites = useFavoritesStore()
  if (data?.conversation) {
    // 1) format conversation
    const conversation = toIConversation(data.conversation)
    // 2) add in conversation store
    if (conversation.users.find(user => [AI_USER, AI_USER_FOR_IS_TYPING].includes(user.login)))
      storeConversationsAURA.setConversation(conversation)
    else
      storeConversations.setConversation(conversation)
    // 3) add in favorite store
    if (data.conversation?.favorite !== -1) {
      storeFavorites.set(
        'externaldm',
        conversation.subject_uri,
        data.conversation.favorite,
      )
    }
    // 4) return the new conversation
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: conversation })
  }
  else {
    throw new TypeError('[newConversation] Missing data.conversation')
  }
}

/**
 * Handles 'getConversations' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.conversations - List of active conversations of current user.
 */

async function getConversations(TxID: TxID, data?: { conversations: RTCConversation[], sections: RTCSection[] }): Promise<void> {
  if (data?.conversations && Array.isArray(data.conversations) || data?.sections) {
    const conversations: IConversation[] = []
    const storeConversations = useConversationsStoreV2()
    const storeConversationsAURA = useConversationsAURAStore()
    const storeFavorites = useFavoritesStore()
    const storeUsers = useUsersStore()
    for (const conversationData of data.conversations) {
      // 1) format conversation
      const conversation = toIConversation(conversationData)
      // 2) add in conversation store
      if (conversation.users.find(user => [AI_USER, AI_USER_FOR_IS_TYPING].includes(user.login)))
        storeConversationsAURA.setConversation(conversation)
      else
        storeConversations.setConversation(conversation)
      // 3) add in favorite store
      if (conversationData?.favorite !== -1) {
        storeFavorites.set(
          'externaldm',
          conversation.subject_uri,
          conversationData.favorite,
        )
      }
      // 4) add in subscriptions store (for subscribe to new content in conversation)
      if (conversationData?.isSubscribed)
        storeUsers.subscriptions.add(conversation.subject_uri)

      // 5) add last message in message store for each unread conversation
      if (conversationData.lastMsg) {
        const msgContent = conversationData.lastMsg?.contents?.[0]

        const listCommentsUrisPromises = (msgContent?.auth && msgContent?.data)
          ? [listCommentsUris(msgContent.auth, [parseContent(msgContent.data)])]
          : undefined
        try {
          const timeline = await toITimeline(
            conversationData.lastMsg,
            conversation,
            TxID,
            listCommentsUrisPromises,
          )
  
          if (!timeline)
            throw new Error(`[getConversations] Failed to format message with id ${conversationData.lastMsg.id}`)
  
          const storeMessages = useContentsStoreV2()
          storeMessages.addMessage(timeline, timeline.thread_id)
        } catch(e) {
          //last message is not added, so it will not be displayed, but allow get conversations to continue
          logger.error('conversationData lastMsg exception', e, conversationData.lastMsg)
        }
          
      }

      conversations.push(conversation)
    }
    // To Do code will uncomment in 25xFD03
    // const storeSections = useSectionsStore()
    // const sections = data.sections.map(section => transformRTCSectionToSection(section))
    // storeSections.sections.set('externaldm', new Map(sections.map(item => [item.id, item])))
    // 5) return the list of all added conversations
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: { conversations, sections: data.sections } })
  }
  else {
    throw new TypeError('[getConversations] Missing data.conversations or data.conversations is not an array')
  }
}

/**
 * Handles 'setConversationHidden' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of conversation.
 * @param data.isHidden - Visibility state of the conversation.
 */
function setConversationHidden(TxID: TxID, data?: { convId: RTCConversationId, isHidden: boolean }) {
  if (data?.convId && Object.hasOwn(data, 'isHidden')) {
    // 1) get conversation in store
    const storeConversations = useConversationsStoreV2()
    const storeConversationsAURA = useConversationsAURAStore()
    const conversation = storeConversations.getConversationById(data.convId.toString())
      || storeConversationsAURA.getConversationById(data.convId.toString())
    if (!conversation)
      throw new Error(`[setConversationHidden] Conversation with id ${data.convId} not found`)
    // 2) set visibility state
    conversation.is_hidden = data.isHidden
    // 3) return visibility state
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: conversation.is_hidden })

    const currentSubjectUri = router.currentRoute.value.params.thread_id
    const currentConversation = storeConversations.getConversationBySubjectUri(currentSubjectUri)
    if (currentConversation?.id === data.convId.toString()) {
    //   // for multi window purpose: when user close a conversation in the main window & a child one is the conversation we close this window
    //   // for multi window purpose: when user close a conversation in a child window we navigate to dm whatsnew
      if (isMultiWindowActive.value) {
        window.close()
      }
      else {
        useLastVisitedConversation().value = ''
        navigate({
          id: 'whatsnew',
          context: EContext.DM,
          replace: true,
        })
      }
    }
  }
  else { throw new TypeError('[setConversationHidden] Missing data.convId or data.isHidden') }
}

/**
 * Handles 'updateConvTitle' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of conversation.
 * @param data.title - Title of the conversation.
 */
function updateConvTitle(TxID: TxID, data?: { convId: RTCConversationId, title: string }) {
  if (data?.convId) {
    // 1) get conversation in store
    const storeConversationsAURA = useConversationsAURAStore()
    const convAURA = storeConversationsAURA.getConversationById(data.convId.toString())

    const store = convAURA ? storeConversationsAURA : useConversationsStoreV2()
    const conversation = store.getConversationById(data.convId.toString())

    if (!conversation)
      throw new Error(`[updateConvTitle] Conversation with id ${data.convId} not found`)
    // 2) set title
    conversation.topic = data.title ?? ''
    // 2) return title
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: conversation.topic })
  }
  else { throw new TypeError('[updateConvTitle] Missing data.convId or data.title') }
}

/**
 * Handles 'readConversation' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.msgId - Id of the message.
 */
function readConversation(TxID: TxID, data?: { convId: RTCConversationId, msgId: RTCMessageId }) {
  if (data?.convId && data?.msgId) {
    // 1) set last message
    const storeConversationsAURA = useConversationsAURAStore()
    const convAURA = storeConversationsAURA.getConversationById(data.convId.toString())

    const store = convAURA ? storeConversationsAURA : useConversationsStoreV2()
    store.updateConversationLastMessageRead(data.convId.toString(), data.msgId.toString())

    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: {} })
  }
  else { throw new TypeError('[readConversation] Missing data.convId or data.msgId') }
}

/**
 * Handles 'newConversationUsers' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.members - List of conversation members.
 */
function newConversationUsers(TxID: TxID, data?: { convId: RTCConversationId, members: RTCUser[] }) {
  if (data?.convId && Array.isArray(data?.members)) {
    // 1) format users
    const members = data.members.map(user => toIUser(user))
    // 2) get conversation in store
    const storeConversations = useConversationsStoreV2()
    const conversation = storeConversations.getConversationById(data.convId.toString())
    if (!conversation)
      throw new Error(`[newConversationUsers] Conversation with id ${data.convId} not found`)
    // 3) add new users in conversation
    const usersToAdd = members.filter(member => conversation.users.findIndex(u => u.id === member.id) === -1)
    conversation.users.push(...usersToAdd)

    // 4) Cache the added users
    const storeUsers = useUsersStore()
    usersToAdd.forEach(user => storeUsers.addSearchedUser({
      full_name: `${user.first_name} ${user.last_name}`,
      login: user.login,
    }))
    // 5) remove re-added users from removed users conversation list
    conversation.removedUsers = (conversation.removedUsers as IUser[])
      .filter(user => usersToAdd.findIndex(u => u.id === user.id) === -1)
    // 6) return members
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: members })
  }
  else { throw new TypeError('[newConversationUsers] Missing data.convId or data.members') }
}

/**
 * Handles 'removeConversationUser' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.userId - Id of the removed user.
 * @param data.members - Updated list of conversation members.
 */
function removeConversationUser(TxID: TxID, data?: {
  convId: RTCConversationId
  userId: RTCUserId
  members: RTCUser[]
}) {
  if (data?.convId && data?.userId) {
    // 1) get conversation in store
    const storeConversations = useConversationsStoreV2()
    const conversation = storeConversations.getConversationById(data.convId.toString())
    if (!conversation)
      throw new Error(`[removeConversationUser] Conversation with id ${data.convId} not found`)
    // 2) remove user and add him in removedUsers list
    const idx = conversation.users.findIndex(u => u.id === data.userId?.toString())
    if (idx !== -1) {
      const removedUser = conversation.users.splice(idx, 1)
      conversation.removedUsers.push(...removedUser)
      RTInterfaceAPI.triggerTxIDEvent({ TxID, data: {} })
    }
    else { throw new Error(`[removeConversationUser] User with id ${data.userId} in conversation with id ${data.convId} not found`) }
  }

  // Only attribute 'convId' in data -> current user just got removed from the conversation
  else if (data?.convId && !data.userId && !data.members) {
    const storeConversations = useConversationsStoreV2()
    const conversation = storeConversations.getConversationById(data.convId.toString())
    const currentSubjectUri = router.currentRoute.value.params.thread_id
    const currentConversation = storeConversations.getConversationBySubjectUri(currentSubjectUri)
    if (!conversation)
      throw new Error(`[removeConversationUser] Conversation with id ${data.convId} not found`)
    storeConversations.removeConversation(conversation)

    // if user is in current conversation now, redirect to next conversation after getting removed, or empty
    if (currentConversation?.id === data.convId.toString()) {
      useLastVisitedConversation().value = ''
      navigate({
        id: 'whatsnew',
        context: EContext.DM,
        replace: true,
      })
    }
  }
  else { throw new TypeError('[removeConversationUser] Missing data.convId or data.userId') }
}

/**
 * Handles 'getConvMembers' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.members - List of conversation members.
 * @param data.removedMembers - List of removed conversation members.
 */
function getConvMembers(TxID: TxID, data?: {
  convId: RTCConversationId
  members: RTCUser[]
  removedMembers?: RTCUser[]
}) {
  if (data?.convId && Array.isArray(data?.members)) {
    // 1) format users
    const users = data.members.map(user => toIUser(user))
    // 2) return members
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: users })
  }
  else { throw new TypeError('[getConvMembers] Missing data.convId or data.members') }
}

/**
 * Handles 'getConversationsByMembers' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.conversations - List of conversations.
 */
function getConversationsByMembers(TxID: TxID, data?: { conversations: RTCConversation[] }) {
  if (Array.isArray(data?.conversations)) {
    const storeConversations = useConversationsStoreV2()
    const conversations: IConversation[] = []
    for (const conversationData of data.conversations) {
      // 1) format conversation
      const conversation = toIConversation(conversationData)
      conversations.push(conversation)
      // 2) add in conversation store
      storeConversations.setConversation(conversation)
    }
    // 3) return conversations
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: conversations })
  }
  else { throw new TypeError('[getConversationsByMembers] Missing data.conversations') }
}

/**
 * Handles 'setConversationFavorite' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.favOrder - Order of the conversation in the user's favorites.
 */
function setConversationFavorite(TxID: TxID, data?:
{ convId: RTCConversationId, favOrder: number, favoriteConversations: RTCFavoriteConversation[] }) {
  if (data?.convId && typeof data.favOrder === 'number') {
    // 1) get conversation in store
    const storeConversations = useConversationsStoreV2()
    const conversation = storeConversations.getConversationById(data.convId.toString())
    if (!conversation)
      throw new Error(`[setConversationFavorite] Conversation with id ${data.convId} not found`)
    // 2) add or update position of the conversation in favorite store
    const storeFavorites = useFavoritesStore()
    const reorderedConversation = data.favoriteConversations.map(conv => transformRTCFavoriteToConversation(conv))
    const newFavoritesListWithPosition = reorderedConversation.reduce((acc, conv) => {
      const convData = storeConversations.getConversationById(conv.convId.toString())
      logger.log('fav subject uri', convData?.subject_uri, conv.position)
      if (convData?.subject_uri) {
        acc.set(convData.subject_uri, conv.position)
      }
      return acc
    }, new Map())
    logger.log('fav newFavoritesListWithPosition', newFavoritesListWithPosition)
    storeFavorites.setAll('externaldm', newFavoritesListWithPosition)
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: reorderedConversation })
  }
  else { throw new TypeError('[setConversationFavorite] Missing data.convId or data.favOrder') }
}

/**
 * Handles 'autoRemoval' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.autoRemovalEnabled - Persistency state of the conversation.
 */
function autoRemoval(TxID: TxID, data?: { convId: RTCConversationId, autoRemovalEnabled: boolean }) {
  if (data?.convId && Object.hasOwn(data, 'autoRemovalEnabled')) {
    // 1) get conversation in store
    const storeConversations = useConversationsStoreV2()
    const conversation = storeConversations.getConversationById(data.convId.toString())
    if (!conversation)
      throw new Error(`[autoRemoval] Conversation with id ${data.convId} not found`)
    // 2) set persistency state
    conversation.is_persistent = !data.autoRemovalEnabled
    // 3) return persistency state
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: conversation.is_persistent })
  }
  else { throw new TypeError('[autoRemoval] Missing data.convId or data.autoRemovalEnabled') }
}

/**
 * Handles 'newMessage' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.message - A newly received message.
 */

async function newMessage(TxID: TxID, data?: { convId: RTCConversationId, message: RTCMessage }) {
  if (data?.convId && data?.message) {
    // 1) retrieve comments (async)
    const listCommentsUrisPromises = (data.message?.contents[0]?.auth && data.message?.contents[0]?.data)
      ? [listCommentsUris(data.message.contents[0].auth, [parseContent(data.message.contents[0].data)])]
      : undefined
    // 2) get conversation in store
    const storeConversationsAURA = useConversationsAURAStore()
    const convAURA = storeConversationsAURA.getConversationById(data.convId.toString())

    const store = convAURA ? storeConversationsAURA : useConversationsStoreV2()
    const conversation = await store.fetchConversation(data.convId.toString())

    if (!conversation)
      throw new Error(`[newMessage] Conversation with id ${data.convId} not found`)
    // 3) format message
    const timeline = await toITimeline(
      data.message,
      conversation,
      TxID,
      listCommentsUrisPromises,
    )

    if (!timeline)
      throw new Error(`[newMessage] Failed to format message with id ${data.message.id}`)
      // 4) add in message store
    const storeMessages = useContentsStoreV2()
    storeMessages.addMessage(timeline, timeline.thread_id)
    // 5) update last message in conversation
    store.updateConversationLastMessage(
      timeline.thread_id,
      timeline.id,
      timeline.created_at,
    )

    // Update recent action id
    if ((timeline.message_details as IMessageDetailsInternalEvent).action) {
      const { action, model_id } = timeline.message_details as IMessageDetailsInternalEvent
      const { updateRecentCollabAction } = useMostRecentCollabActions()
      updateRecentCollabAction({
        coReviewEvent: action,
        conversationId: timeline.thread_id,
        messageId: model_id,
      })
    }

    const storeUsers = useUsersStore()
    if (storeUsers.currentUser?.login === timeline.author.login)
      store.updateConversationLastMessageRead(data.convId.toString(), timeline.id)
    // 6) return the new message
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: timeline })
  }
  else {
    throw new TypeError('[newMessage] Missing data.convId or data.message')
  }
}

/**
 * Handles 'getMessages' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.messages - Partial list of messages in the conversation.
 * @param data.userId - Current user id.
 * @param data.jwtTokens - JWT tokens to retrieve comments in the list of messages.
 * @param data.text - Filter query.
 */

async function getMessages(TxID: TxID, data?: {
  convId: RTCConversationId
  messages: RTCMessage[]
  userId: RTCUserId
  jwtTokens?: string[]
  text?: string
}) {
  if (data?.convId) {
    // 0) if no messages, just return with empty list
    if (!data.messages || !(Array.isArray(data.messages)) || data.messages.length === 0)
      return RTInterfaceAPI.triggerTxIDEvent({ TxID, data: [] })

    const storeComments = useCommentsStoreV2()
    storeComments.setCurrentUser(data.userId.toString())
    // 1) retrieve comments (async)
    // first construct an array of comment uris
    const commentUrisArray = data.messages.flatMap((message) => {
      return message.contents
        .filter(content => content.type === 'comment') // keep only comment messages
        .map(content => parseContent(content.data)) // decode base64 to retrieve the comment uri
    })
    const listCommentsUrisPromises = data.jwtTokens
      ? data.jwtTokens.map(jwtToken => listCommentsUris(jwtToken, commentUrisArray))
      : undefined
    // 2) get conversation in store
    const storeConversationsAURA = useConversationsAURAStore()
    const convAURA = storeConversationsAURA.getConversationById(data.convId.toString())

    const store = convAURA ? storeConversationsAURA : useConversationsStoreV2()
    const conversation = await store.fetchConversation(data.convId.toString())

    if (!conversation)
      throw new Error(`[newMessage] Conversation with id ${data.convId} not found`)
    // 3) format message & add endorses
    const promises: Promise<ITimeline | undefined>[] = []
    for (const message of data.messages) {
      promises.push(toITimeline(
        message,
        conversation,
        TxID,
        listCommentsUrisPromises,
      ))
      storeComments.setEndorses(message.id.toString(), message.likes.map(l => l.toString()))
    }
    // 4) add in message store and return messages
    const timelines: Array<ITimeline> = []
    const storeMessages = useContentsStoreV2()
    Promise.allSettled(promises)
      .then((results) => {
        for (const result of results) {
          if (result.status !== 'fulfilled' || !result.value)
            continue
          timelines.push(result.value)
          /* Display messages page by page instead of one by one. */
          storeMessages.addMessage(
            result.value,
            result.value.thread_id,
            data.text,
          )
          /* Display messages one by one instead of page by page. */
          // storeMessages.addMessage(result.value, result.value.thread_id)
        }
        RTInterfaceAPI.triggerTxIDEvent({ TxID, data: timelines })
      })
  }
  else { throw new TypeError('[getMessage] Missing data.convId or data.messages') }
}

/**
 * Handles 'editMessage' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.message - Edited message.
 */
async function editMessage(TxID: TxID, data?: { convId: RTCConversationId, message: RTCMessage }) {
  if (data?.message?.contents[0] && data?.message?.editionDate) {
    if (data.message.contents[0].type !== 'text')
      throw new Error('[editMessage] Not a text message')
    // 1) edit message text and edition date
    const storeMessages = useContentsStoreV2()
    storeMessages.editMessageText({
      message_id: data.message.id.toString(),
      text: data.message.contents[0].data,
      edition_date: data.message.editionDate,
    })

    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: {} })
  }
  else { throw new TypeError('[editMessage] Missing data.message.contents or data.message.editionDate') }
}

/**
 * Handles 'deleteMessage' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.deletedMessage - Deleted message id.
 * @param data.deletedContents - Deleted contents of the message.
 * @param data.lastMessage - Previous last message if the deleted message was the last message of the conversation.
 */
function deleteMessage(TxID: TxID, data?: {
  convId: RTCConversationId
  deletedMessage: RTCMessageId
  deletedContents: RTCContentId[]
  lastMessage?: Partial<RTCMessage>
}): void {
  if (!data?.convId || !data?.deletedMessage)
    throw new TypeError('[deleteMessage] Missing data.convId or data.deletedMessage')

  // 1) delete message in store
  const storeMessages = useContentsStoreV2()
  storeMessages.deleteMessage(data.deletedMessage.toString(), data.convId.toString())

  // 2) delete endorses in store
  const storeComments = useCommentsStoreV2()
  storeComments.deleteAllEndorses(data.deletedMessage.toString())

  // 3) if deleted message is last message of conversation, set previous message as last message
  // if the conversation is empty, the last message is undefined
  const storeConversations = useConversationsStoreV2()
  const storeConversationsAURA = useConversationsAURAStore()
  const lastMessage = data?.lastMessage

  storeConversations.updateConversationLastMessage(
    data.convId.toString(),
    lastMessage?.id?.toString(),
    lastMessage?.creationDate,
  )

  storeConversationsAURA.updateConversationLastMessage(
    data.convId.toString(),
    lastMessage?.id?.toString(),
    lastMessage?.creationDate,
  )

  // NB: contents deletion handled by 3DSwym back-end
  RTInterfaceAPI.triggerTxIDEvent({ TxID, data: {} })
}

/**
 * Handles 'likeMessage' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.msgId - Id of the message that was liked/disliked.
 * @param data.userId - Id of the user who liked/disliked.
 * @param data.liked - If the message was liked or disliked.
 */
function likeMessage(TxID: TxID, data?: {
  convId: RTCConversationId
  msgId: RTCMessageId
  userId: RTCUserId
  liked: boolean
}) {
  if (data?.msgId && data?.userId) {
    // 1) add or remove endorse in comment store
    const storeComments = useCommentsStoreV2()
    if (data?.liked)
      storeComments.addEndorse(data?.msgId.toString(), data?.userId.toString())
    else storeComments.deleteEndorse(data?.msgId.toString(), data?.userId.toString())
    // 2) return the user who liked (to set endorse uri)
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: data?.userId.toString() })
  }
  else { throw new TypeError('[likeMessage] Missing data.userId or data.msgId') }
}

/**
 * Handles 'getLikes' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.msgId - Id of the message that was liked/disliked.
 * @param data.users - List of users who liked the message.
 */
function getLikes(TxID: TxID, data?: {
  convId: RTCConversationId
  msgId: RTCMessageId
  users: RTCUser[]
}) {
  if (data?.convId && data?.msgId && Array.isArray(data?.users)) {
    // 1) set message endorses in comment store
    const storeComments = useCommentsStoreV2()
    storeComments.setEndorses(data.msgId.toString(), data.users.map(user => user.id.toString()))
    // 2) return the list of users who liked the message
    const usersWhoEndorse = data.users.map(user => toIUser(user))
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: usersWhoEndorse })
  }
  else { throw new TypeError('[getLikes] Missing data.convId or data.msgId or data.users or data.userId') }
}

/**
 * Handles 'setConversationSubscribed' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.subjectUri - Conversation subject uri.
 * @param data.isSubscribed - If the current user has subcribed to new content publish notifications.
 */
function setConversationSubscribed(TxID: TxID, data?: {
  convId: RTCConversationId
  subjectUri: SUBJECT_6W_URI
  isSubscribed: boolean
}) {
  if (data?.subjectUri && Object.hasOwn(data, 'isSubscribed')) {
    // 1) set user subscription to new content published notifications
    const storeUsers = useUsersStore()
    if (data.isSubscribed)
      storeUsers.subscriptions.add(data.subjectUri)
    else
      storeUsers.subscriptions.delete(data.subjectUri)
    // NB: this is for 'Subscribe to new content' in conversation only, NOT content activities (handled by 3DSwym)
    // 2) return subscription state
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: data.isSubscribed })
  }
  else { throw new TypeError('[setConversationSubscribed] Missing data.subjectUri or data.isSubscribed') }
}

/**
 * Handles 'setConversationMuted' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 * @param data.subjectUri - Conversation subject uri.
 * @param data.isMuted - If the current user has mute content, notification will off.
 */
function setConversationMuted(TxID: TxID, data?: {
  convId: RTCConversationId
  isMuted: boolean
  subjectUri: SUBJECT_6W_URI
}) {
  if (data?.subjectUri && Object.hasOwn(data, 'isMuted')) {
    const storeConversationsV2 = useConversationsStoreV2()
    const conversation = storeConversationsV2.getConversationById(data.convId.toString())
    if (!conversation)
      throw new Error(`[setConversationMuted] Conversation with id ${data.convId} not found`)
    conversation.is_muted = data.isMuted

    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: data.isMuted })
  }
  else { throw new TypeError('[setConversationMuted] Missing data.subjectUri or data.isSubscribed') }
}

/**
 * Handles 'missedBadge' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.count - Number of missed called, value of 'Calls' badge.
 */
function missedBadge(TxID: TxID, data?: { count: number }) {
  if (data && Object.hasOwn(data, 'count')) {
    const storeConversations = useConversationsStoreV2()
    // 1) set or reset missed calls badge
    storeConversations.countMissedCalls = Number(data?.count || 0) || 0
    // 2) return badge value
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: storeConversations.countMissedCalls })
  }
  else { throw new Error('[missedBadge] Missing data.count') }
}

/**
 * Handles 'refreshPreview' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.subjectUri - Content subject uri.
 * @param data.processingStatus - Status of content processing.
 */
function refreshPreview(TxID: TxID, data?: { subjectUri: SUBJECT_6W_URI, processingStatus: string }) {
  if (data?.subjectUri && data?.processingStatus) {
    // if processing status has completed, fetch the content data to retrieve preview fingerprint
    if (data.processingStatus === 'completed') {
      const storeContents = useContentsStore()
      storeContents.fetchContent(data.subjectUri, true)
    }
  }
  else { throw new Error('[missedBadge] Missing data.subjectUri or data.processingStatus') }
}

/**
 * Handles 'deleteConversation' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 */
function deleteConversation(TxID: TxID, data?: {
  convId: RTCConversationId
}) {
  if (data?.convId) {
    const storeConversations = useConversationsStoreV2()
    const conversation = storeConversations.getConversationById(data.convId.toString())

    if (conversation) {
      const currentSubjectUri = router.currentRoute.value.params.thread_id
      const currentConversation = storeConversations.getConversationBySubjectUri(currentSubjectUri)

      storeConversations.removeConversation(conversation)

      if (currentConversation?.id === data.convId.toString()) {
        //   // for multi window purpose: when user close a conversation in the main window & a child one is the conversation we close this window
        //   // for multi window purpose: when user close a conversation in a child window we navigate to dm whatsnew
        if (isMultiWindowActive.value) {
          window.close()
        }
        else {
          useLastVisitedConversation().value = ''
          navigate({
            id: 'whatsnew',
            context: EContext.DM,
            replace: true,
          })
        }
      }
    }
  }
  else { throw new TypeError('[setConversationSubscribed] Missing data.convId') }
}

/**
 * Handles 'deleteConversation' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation.
 */
function getUser(TxID: TxID, data?: {
  user: RTCUser
}) {
  if (data?.user) {
    const user = toIUser(data.user)
    const storeUsers = useUsersStoreV2()
    storeUsers.setUser(data.user.id, user)
    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: user })
  }
  else { throw new TypeError('[setConversationSubscribed] Missing data.user') }
}

/**
 * Handles 'removeLegacyConv' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.migratedSwymId - Id of the legacy conversation to delete
 */
async function removeLegacyConv(TxID: TxID, data?: {
  migratedSwymId: string
}) {
  const { useTranslations } = await import('~/features-generic/translations/composables/translations')
  const { $i18n, promise } = useTranslations('real-time-enhancements')

  if (data?.migratedSwymId) {
    const storeConversationsV1 = useConversationsStoreV1()
    const subjectUri = storeConversationsV1.getSubjectUriFromId(data?.migratedSwymId as string)
    // @ts-expect-error this error can be safely ignored
    if (router.currentRoute.value.params.thread_id !== subjectUri) {
      storeConversationsV1.threads.delete(subjectUri)
    }
    else {
      await promise
      const bus = useEventBus('swym-app')
      const modalProps: ModalConfirmProps = {
        title: $i18n('RefreshTitle'),
        content: $i18n('RefreshPageContent'),
        confirmLabel: $i18n('Refresh'),
      }

      bus.emit('modal-manager:open', {
        component: 'modal-confirm',
        props: modalProps,
      })
      bus.once((event, payload) => {
        if (payload) {
          window.location.reload()
        }
      })
    }

    RTInterfaceAPI.triggerTxIDEvent({ TxID, data: data.migratedSwymId })
  }
  else {
    throw new TypeError('[removeLegacyConv] missing data.migratedSwymId')
  }
}

/**
 * Handles 'createSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.section - Get section data .
 */
function createSection(TxID: TxID, data?: {
  section: RTCSection
}) {
  const storeSections = useSectionsStore()
  if (!data?.section) {
    throw new TypeError('[createSection] Missing data.section')
  }
  const section = transformRTCSectionToSection(data.section)
  storeSections.sections.get('externaldm')
    ?.set(section.id, section)
}

/**
 * Handles 'deleteSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.sectionId - Get deleted section id.
 */
function deleteSection(TxID: TxID, data?: {
  sectionId: string
}) {
  if (!data?.sectionId) {
    throw new TypeError('[deleteSection] Missing data.sectionId')
  }
  // Rest of the handling done in section store
}

/**
 * Handles 'renameSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.section - Get updated name section data .
 */
function renameSection(TxID: TxID, data?: {
  section: RTCSection
}) {
  if (!data?.section) {
    throw new TypeError('[renameSection] Missing data.section')
  }
  // Rest of the handling done in section store
}

/**
 * Handles 'addToSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.convId - Id of the conversation to add in the section.
 * @param data.position - Position where to insert the conversation in the section
 * @param data.sectionId - Id of the section the conversation will be added to
 */
function addToSection(TxID: TxID, data?: {
  convId: number
  position: number
  sectionId: number
}) {
  if (!data) {
    throw new TypeError('[addToSection] Missing data.convId')
  }
  // Rest of the handling done in section store
}

/**
 * Handles 'removeFromSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.conversations - The new list of conversations in the section after deletion .
 */
function removeFromSection(TxID: TxID, data?: {
  conversations: RTCSectionThread[]
}) {
  if (!data?.conversations) {
    throw new TypeError('[removeFromSection] Missing data.conversations')
  }
  // Rest of the handling done in section store
}

/**
 * Handles 'reorderSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.section - Get section data .
 */
function reorderSection(TxID: TxID, data: {
  sections: RTCSection[]
}) {
  const storeSections = useSectionsStore()
  if (!data?.sections) {
    throw new TypeError('[reorderSection] Missing data.sections')
  }
  const dmSection = storeSections.sections.get('externaldm')
  data.sections.forEach((section) => {
    dmSection?.set(section.id.toString(), {
      ...dmSection.get(section.id.toString()),
      ...section,
    })
  })
}

/**
 * Handles 'reorderInSection' event response.
 * @param TxID - Transaction ID.
 * @param data - Data received from RTC server.
 * @param data.conversations - Get conversations data .
 */
function reorderInSection(TxID: TxID, data: {
  conversations: RTCSectionThread[]
}) {
  if (!data.conversations) {
    throw new TypeError('[reorderInSection] Missing data.conversations')
  }
  RTInterfaceAPI.triggerTxIDEvent({ TxID, data: data.conversations })
}

/**
 * Handles RTC response events.
 * @param response - Event received from RTC server.
 */
function RTInterfaceResponseHandler(response: IRTInterfaceResponseData) {
  const eventHandlers = {
    // conversation
    newConversation,
    getConversations,
    setConversationHidden,
    updateConvTitle,
    readConversation,
    newMessage,
    getMessages,
    newConversationUsers,
    removeConversationUser,
    getConvMembers,
    getConversationsByMembers,
    setConversationFavorite,
    autoRemoval,
    editMessage,
    deleteMessage,
    likeMessage,
    getLikes,
    setConversationSubscribed,
    setConversationMuted,
    refreshPreview,
    deleteConversation,
    getUser,
    removeLegacyConv,
    // call history
    missedBadge,
    // collab
    startCatiaCollab,
    RTCMessengerCoReviewStop,
    RTCMessengerCoReviewLeft,
    RTCMessengerReplyToCoreview,
    RTCMessengerAcceptLead,

    // sections
    createSection,
    deleteSection,
    renameSection,
    addToSection,
    removeFromSection,
    reorderSection,
    reorderInSection,
  }

  if (!eventHandlers[response.action])
    return // unsupported action
  try {
    if (response.error)
      throw response.error
    eventHandlers[response.action](response.TxID, response.data)
  }
  catch (error) {
    const errorMessage = (error instanceof Error) ? error.message : String(error)
    RTInterfaceAPI.triggerTxIDEvent({ TxID: response.TxID, error: response.error ?? errorMessage })
    logger.error(`[RTInterfaceResponseHandler] Error when processing response: ${error}`)

    // For newMessage, we need to set the temp message as error.
    if (response.action === 'newMessage') {
      const storeMessages = useContentsStoreV2()
      storeMessages.mergeMessage({ message_uri: response.TxID, status: TimelineStatus.ERROR })
    }
  }
}

/**
 * Initializes the events listener.
 */
let alreadyInitialized = false
export function useRTInterfaceResponseHandler() {
  if (!alreadyInitialized) {
    RTInterfaceAPI.addCallback('conversation', RTInterfaceResponseHandler)
    RTInterfaceAPI.addCallback('call', RTInterfaceResponseHandler)

    RTInterfaceAPI.addReconnectCallback(onReconnect)

    alreadyInitialized = true
  }
}

/**
 * On websocket reconnect, refetch conversations and messages.
 */
async function onReconnect(lastDisconnectTime: number) {
  const store = useConversationsStoreV2()

  const getConversationsPromise = RTConv.getConversations({ waitForResponse: true })
  const currentConvId = getIdFromSubjectUri(useContentsStoreV2().thread_selected)
  if (currentConvId && Number.parseInt(currentConvId))
    RTConv.getMessages({ convId: Number.parseInt(currentConvId), waitForResponse: false })
  const convData = (await getConversationsPromise).conversations

  // Event handler for reconnection
  // 1) Event: got removed from conversations during disconnection
  // By comparing old ConvIds('treadsId') and new ConvIds(event 'getConversations')
  const newConvIds = convData.map((item: IConversation) => item.id)
  const removedConvs = store.threadsId.filter(convId => !newConvIds.includes(convId))

  // This means user got removed from conversation(s) during disconnection, we need to remove it/them now ourself.
  if (removedConvs && removedConvs.length > 0) {
    let isNeedNavigate = false
    for (let i = 0; i < removedConvs.length; i++) {
      const removedConvId = removedConvs[i]
      const removedConv = store.getConversationById(removedConvId)
      if (removedConv) {
        store.removeConversation(removedConv)

        // if user is in current removed conversation, we need to redirect to next existed conversation, or empty
        if (currentConvId.toString() === removedConvId)
          isNeedNavigate = true
      }
    }

    if (isNeedNavigate) {
      useLastVisitedConversation().value = ''
      navigate({
        id: 'whatsnew',
        context: EContext.DM,
        replace: true,
      })
    }
  }

  // 2) For desktop app: check if some conversations have received new messages during disconnect timeframe.
  // If so, send an event to inform the desktop app.
  const newContent = convData.some((c: IConversation) => store.getConversationTime(c) > lastDisconnectTime)
  if (newContent) {
    const REALTIME_NEW_CONTENT_AVAILABLE_TOPIC = 'tenant_{tenant}_new_rtc_content'
    const PlatformAPI = await requirejsOne<PlatformAPI_AMD>('DS/PlatformAPI/PlatformAPI')
    const platformId = getPlatformId()
      .toLowerCase()
    const topic = REALTIME_NEW_CONTENT_AVAILABLE_TOPIC.replace('{tenant}', platformId)
    PlatformAPI.publish(topic, {
      message_type: 'NEW_CONTENT',
      content_type: 'message/notification',
    })
  }
}
